import React, { useState } from 'react';
import { useUserLimits } from '../hooks/useUserLimits';
import { useQuery } from '@tanstack/react-query';
import { billingApiService } from '../api/billingApi';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Crown, Star, Zap, ArrowUpRight } from 'lucide-react';
import { usePlans, getPlanByTierLegacy } from '../config/plans';
import SubscriptionPlansModal from './billing/SubscriptionPlansModal';

interface PlanIndicatorProps {
    className?: string;
}

const PlanIndicator: React.FC<PlanIndicatorProps> = ({ className = '' }) => {
    const { data: limits } = useUserLimits();
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const plans = usePlans();

    // Fetch subscription info when modal is opened
    const { data: subscriptionData } = useQuery({
        queryKey: ['subscription'],
        queryFn: () => billingApiService.getSubscriptionInfo(),
        enabled: showUpgradeModal, // Only fetch when modal is open
    });

    const getTierIcon = (tier: string) => {
        switch (tier) {
            case 'ultra':
                return <Crown className="h-4 w-4" />;
            case 'premium':
                return <Star className="h-4 w-4" />;
            default:
                return <Zap className="h-4 w-4" />;
        }
    };

    const getTierColor = (tier: string) => {
        switch (tier) {
            case 'ultra':
                return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
            case 'premium':
                return 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getTierName = (tier: string) => {
        const plan = getPlanByTierLegacy(tier, plans);
        return plan ? plan.name : 'Free';
    };

    if (!limits) {
        return null;
    }

    const usagePercentage = Math.min((limits.monthlyUsed / limits.monthlyLimit) * 100, 100);
    const isNearLimit = usagePercentage >= 80;
    const isAtLimit = usagePercentage >= 100;

    return (
        <>
            <div className={`flex items-center gap-2 ${className}`}>
                {/* Plan Badge - Clickable */}
                <Badge
                    className={`font-medium text-xs h-7 px-2 ${getTierColor(limits.tier)} cursor-pointer hover:opacity-80 transition-opacity`}
                    onClick={() => setShowUpgradeModal(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setShowUpgradeModal(true);
                        }
                    }}
                >
                    <div className="flex items-center gap-1">
                        {getTierIcon(limits.tier)}
                        {(isNearLimit) ? 'Upgrade' : getTierName(limits.tier)}
                    </div>
                </Badge>

                {/* Usage Progress Bar - Only show when approaching or at limit */}
                {isNearLimit && (
                    <div className="flex items-center gap-2">
                        <div className="relative w-8 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                                className={`absolute top-0 left-0 h-full rounded-full transition-all duration-300 ${isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-orange-500' : 'bg-green-500'
                                    }`}
                                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                            />
                        </div>
                        {/* Show numbers only on larger screens when at/near limit */}
                        <span className="hidden md:inline text-xs text-gray-600">
                            {limits.monthlyUsed}/{limits.monthlyLimit}
                        </span>
                    </div>
                )}

                {/* Upgrade Button - Only for trial tier */}
                {limits.tier === 'trial' && (
                    <Button
                        onClick={() => setShowUpgradeModal(true)}
                        size="sm"
                        className="h-7 px-2 text-xs bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                    >
                        <ArrowUpRight className="h-3 w-3 md:mr-1" />
                        <span className="hidden md:inline">
                            Upgrade
                        </span>
                    </Button>
                )}
            </div>

            {/* Upgrade Modal */}
            <SubscriptionPlansModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                currentTier={limits.tier}
                subscriptionInfo={subscriptionData?.success ? subscriptionData.subscription : undefined}
                onSubscriptionChange={() => {
                    // The modal will handle its own state updates
                }}
            />
        </>
    );
};

export default PlanIndicator;
